'use client';

import { useEffect, useRef, useState } from 'react';

type OutputFormat = 'png' | 'jpeg' | 'webp';
type ResizeMode = 'dimensions' | 'percent';

const formatMeta: Record<
  OutputFormat,
  { label: string; mime: string; ext: string; quality: boolean }
> = {
  png: { label: 'PNG', mime: 'image/png', ext: 'png', quality: false },
  jpeg: { label: 'JPEG', mime: 'image/jpeg', ext: 'jpg', quality: true },
  webp: { label: 'WebP', mime: 'image/webp', ext: 'webp', quality: true },
};

function getBaseName(name: string) {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

export default function BrowserImageResizeTool() {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [fileName, setFileName] = useState('image');
  const [originalWidth, setOriginalWidth] = useState(0);
  const [originalHeight, setOriginalHeight] = useState(0);
  const [mode, setMode] = useState<ResizeMode>('dimensions');
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [percent, setPercent] = useState(50);
  const [lockAspect, setLockAspect] = useState(true);
  const [format, setFormat] = useState<OutputFormat>('png');
  const [quality, setQuality] = useState(0.92);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  function handleFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setImage(img);
      setOriginalWidth(img.naturalWidth);
      setOriginalHeight(img.naturalHeight);
      setWidth(img.naturalWidth);
      setHeight(img.naturalHeight);
      setFileName(getBaseName(file.name) || 'image');
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      setError('This image format is not supported in your browser.');
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }

  const targetWidth =
    mode === 'percent'
      ? Math.max(1, Math.round((originalWidth * percent) / 100))
      : Math.max(1, width || 1);
  const targetHeight =
    mode === 'percent'
      ? Math.max(1, Math.round((originalHeight * percent) / 100))
      : Math.max(1, height || 1);

  useEffect(() => {
    if (!image || !canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, targetWidth, targetHeight);
    ctx.drawImage(image, 0, 0, targetWidth, targetHeight);
    const active = formatMeta[format];
    canvas.toBlob(
      (blob) => {
        if (blob) {
          setDownloadUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(blob);
          });
        }
      },
      active.mime,
      active.quality ? quality : undefined,
    );
  }, [image, targetWidth, targetHeight, format, quality]);

  function handleWidthChange(next: number) {
    const value = Math.max(1, Math.round(next) || 1);
    setWidth(value);
    if (lockAspect && originalWidth > 0) {
      setHeight(Math.max(1, Math.round((value * originalHeight) / originalWidth)));
    }
  }

  function handleHeightChange(next: number) {
    const value = Math.max(1, Math.round(next) || 1);
    setHeight(value);
    if (lockAspect && originalHeight > 0) {
      setWidth(Math.max(1, Math.round((value * originalWidth) / originalHeight)));
    }
  }

  const active = formatMeta[format];

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Resize</span>
        <h2 className="tool-live-title">Resize an image</h2>
        <p className="tool-live-subtitle">
          Upload an image and resize it by exact pixels or by percentage. Everything runs in your
          browser and nothing is uploaded to a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose an image</span>
            <input
              accept="image/*"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>

          {image ? (
            <div className="tool-live-controls">
              <label className="tool-live-field tool-live-field-full">
                <span>Resize by</span>
                <select
                  onChange={(event) => setMode(event.target.value as ResizeMode)}
                  value={mode}
                >
                  <option value="dimensions">Exact dimensions</option>
                  <option value="percent">Percentage</option>
                </select>
              </label>

              {mode === 'dimensions' ? (
                <>
                  <label className="tool-live-field">
                    <span>Width (px)</span>
                    <input
                      min={1}
                      onChange={(event) => handleWidthChange(Number(event.target.value))}
                      type="number"
                      value={width}
                    />
                  </label>
                  <label className="tool-live-field">
                    <span>Height (px)</span>
                    <input
                      min={1}
                      onChange={(event) => handleHeightChange(Number(event.target.value))}
                      type="number"
                      value={height}
                    />
                  </label>
                  <label className="tool-live-field tool-live-field-full">
                    <span>
                      <input
                        checked={lockAspect}
                        onChange={(event) => setLockAspect(event.target.checked)}
                        type="checkbox"
                      />{' '}
                      Lock aspect ratio
                    </span>
                  </label>
                </>
              ) : (
                <label className="tool-live-field tool-live-field-full">
                  <span>Scale: {percent}%</span>
                  <input
                    max={200}
                    min={1}
                    onChange={(event) => setPercent(Number(event.target.value))}
                    step={1}
                    type="range"
                    value={percent}
                  />
                </label>
              )}

              <label className="tool-live-field">
                <span>Format</span>
                <select
                  onChange={(event) => setFormat(event.target.value as OutputFormat)}
                  value={format}
                >
                  <option value="png">PNG</option>
                  <option value="jpeg">JPEG</option>
                  <option value="webp">WebP</option>
                </select>
              </label>
              {active.quality ? (
                <label className="tool-live-field">
                  <span>Quality: {Math.round(quality * 100)}%</span>
                  <input
                    max={1}
                    min={0.3}
                    onChange={(event) => setQuality(Number(event.target.value))}
                    step={0.02}
                    type="range"
                    value={quality}
                  />
                </label>
              ) : null}
            </div>
          ) : null}

          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download={`${fileName}-${targetWidth}x${targetHeight}.${active.ext}`}
                href={downloadUrl}
              >
                Download {active.label}
              </a>
            ) : null}
          </div>

          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {image ? null : <p className="tool-live-panel-note">Pick an image to resize it.</p>}
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image ? 'block' : 'none',
            }}
          />
          {image ? (
            <p className="tool-live-panel-note">
              Original: {originalWidth} × {originalHeight} px → New: {targetWidth} × {targetHeight}{' '}
              px
            </p>
          ) : null}
          <p className="tool-live-panel-note">
            Processing runs locally with an HTML canvas. Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
